import { useState, useEffect } from 'react';
import {
  fetchNotificationConfig,
  updateNotificationConfig,
  testNotification,
  fetchNotificationLogs,
  fetchMemberApplications,
  approveMemberApplication,
  rejectMemberApplication,
  type AdminNotificationConfig,
  type NotificationLogEntry,
  type MemberApplication,
} from '../api/client';

export function NotificationsTab() {
  const [config, setConfig] = useState<AdminNotificationConfig | null>(null);
  const [adminEmail, setAdminEmail] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(465);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [ntfyTopic, setNtfyTopic] = useState('');
  const [alerts, setAlerts] = useState({
    memberSignup: true,
    riskGovernor: true,
    examPassed: true,
    investorBooking: true,
  });

  const [savingConfig, setSavingConfig] = useState(false);
  const [configSuccess, setConfigSuccess] = useState('');
  const [configError, setConfigError] = useState('');

  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const [applications, setApplications] = useState<MemberApplication[]>([]);
  const [appCounts, setAppCounts] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending_approval' | 'approved' | 'rejected'>('all');
  const [loadingApps, setLoadingApps] = useState(false);
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [issuedCode, setIssuedCode] = useState<{ name: string; code: string } | null>(null);

  const [logs, setLogs] = useState<NotificationLogEntry[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Manual intake form state
  const [showManualIntake, setShowManualIntake] = useState(false);
  const [intakeName, setIntakeName] = useState('');
  const [intakeEmail, setIntakeEmail] = useState('');
  const [intakeTier, setIntakeTier] = useState('Tier 2: Balanced Momentum');
  const [intakeCapital, setIntakeCapital] = useState('50000');
  const [intakeNotes, setIntakeNotes] = useState('');
  const [submittingIntake, setSubmittingIntake] = useState(false);

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = () => {
    loadConfig();
    loadApplications();
    loadLogs();
  };

  const loadConfig = async () => {
    try {
      const res = await fetchNotificationConfig();
      setConfig(res.data);
      setAdminEmail(res.data.notifyEmailTo || '');
      setSmtpHost(res.data.smtpHost || '');
      setSmtpPort(res.data.smtpPort || 465);
      setSmtpUser(res.data.smtpUser || '');
      setNtfyTopic(res.data.notifyNtfyTopic || '');
      if (res.data.enabledAlerts) {
        setAlerts(res.data.enabledAlerts);
      }
    } catch (err: unknown) {
      console.warn('Failed to load notification config:', err);
    }
  };

  const loadApplications = async (status?: string) => {
    setLoadingApps(true);
    try {
      const res = await fetchMemberApplications(status && status !== 'all' ? status : undefined);
      setApplications(res.data);
      setAppCounts(res.counts);
    } catch (err: unknown) {
      console.warn('Failed to load applications:', err);
    } finally {
      setLoadingApps(false);
    }
  };

  const loadLogs = async () => {
    setLoadingLogs(true);
    try {
      const res = await fetchNotificationLogs(30);
      setLogs(res.data);
    } catch (err: unknown) {
      console.warn('Failed to load notification logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingConfig(true);
    setConfigSuccess('');
    setConfigError('');
    try {
      const payload: any = {
        notifyEmailTo: adminEmail.trim(),
        smtpHost: smtpHost.trim(),
        smtpPort: Number(smtpPort) || 465,
        smtpUser: smtpUser.trim(),
        notifyNtfyTopic: ntfyTopic.trim(),
        enabledAlerts: alerts,
      };
      if (smtpPass.trim()) {
        payload.smtpPass = smtpPass.trim();
      }
      const res = await updateNotificationConfig(payload);
      setConfig(res.data);
      setConfigSuccess('Notification settings successfully updated!');
      setSmtpPass('');
      setTimeout(() => setConfigSuccess(''), 4000);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setConfigError(`Failed to save settings: ${msg}`);
    } finally {
      setSavingConfig(false);
    }
  };

  const handleSendTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const target = testEmail.trim() || adminEmail.trim();
      const res = await testNotification(target || undefined);
      setTestResult({
        success: res.success,
        message: res.message,
      });
      loadLogs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setTestResult({
        success: false,
        message: `Test failed: ${msg}`,
      });
    } finally {
      setTesting(false);
    }
  };

  const handleApprove = async (app: MemberApplication) => {
    setActionMessage(null);
    setIssuedCode(null);
    try {
      const res = await approveMemberApplication(app.id);
      setActionMessage({
        type: 'success',
        text: `Approved ${app.name}! Invite code generated and welcome email dispatched.`,
      });
      setIssuedCode({ name: app.name, code: res.inviteCode });
      loadApplications(filterStatus);
      loadLogs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage({ type: 'error', text: `Failed to approve application: ${msg}` });
    }
  };

  const handleReject = async (app: MemberApplication) => {
    const reason = window.prompt(`Enter rejection reason for ${app.name}:`, 'Does not meet current risk mandate criteria');
    if (reason === null) return;
    setActionMessage(null);
    try {
      await rejectMemberApplication(app.id, reason);
      setActionMessage({ type: 'success', text: `Rejected application for ${app.name}.` });
      loadApplications(filterStatus);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage({ type: 'error', text: `Failed to reject: ${msg}` });
    }
  };

  const handleManualIntake = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!intakeName || !intakeEmail) return;
    setSubmittingIntake(true);
    try {
      const res = await fetch('/api/v1/member/apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: intakeName,
          email: intakeEmail,
          riskTier: intakeTier,
          targetInvestment: Number(intakeCapital) || 50000,
          notes: intakeNotes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit application');
      setActionMessage({
        type: 'success',
        text: `Applicant ${intakeName} registered! Admin email notification dispatched.`,
      });
      setIntakeName('');
      setIntakeEmail('');
      setIntakeNotes('');
      setShowManualIntake(false);
      loadApplications(filterStatus);
      loadLogs();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setActionMessage({ type: 'error', text: msg });
    } finally {
      setSubmittingIntake(false);
    }
  };

  const filteredApps = filterStatus === 'all'
    ? applications
    : applications.filter((a) => a.status === filterStatus);

  return (
    <div className="tab-content notifications-tab">
      {/* Top Banner Alert / Action Message */}
      {actionMessage && (
        <div className={`card mb-20 ${actionMessage.type === 'success' ? 'bg-green-subtle text-green' : 'error-state'}`} style={{ padding: '14px 18px' }}>
          <strong>{actionMessage.type === 'success' ? '✅ Success' : '⚠️ Error'}:</strong> {actionMessage.text}
        </div>
      )}

      {/* Issued Code High-Visibility Alert */}
      {issuedCode && (
        <div className="card mb-20" style={{ backgroundColor: 'rgba(16, 185, 129, 0.15)', borderColor: '#10b981', padding: '18px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <h4 style={{ color: '#10b981', margin: '0 0 4px 0' }}>🎉 Invite Code Generated for {issuedCode.name}</h4>
              <p style={{ margin: 0, color: '#f8fafc', fontSize: '14px' }}>
                The applicant has been emailed their credentials. You can also provide this code directly:
              </p>
            </div>
            <div style={{ background: '#0b0f19', padding: '8px 16px', borderRadius: '6px', border: '1px solid #10b981', fontFamily: 'monospace', fontSize: '18px', fontWeight: 'bold', color: '#38bdf8', letterSpacing: '1px' }}>
              {issuedCode.code}
            </div>
          </div>
        </div>
      )}

      {/* Grid: Admin Settings & Approvals Summary */}
      <div className="grid-2-col mb-20" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Card 1: Admin Email & Dispatch Settings */}
        <div className="card">
          <div className="card-head">
            <div>
              <h3 className="card-title">📧 Admin Notification Channels</h3>
              <p className="card-subtitle text-muted" style={{ margin: '4px 0 0 0' }}>
                Configure recipient email and alerts requiring administrator attention.
              </p>
            </div>
            <span className={`badge ${config?.isSmtpConfigured ? 'badge--green' : 'badge--yellow'}`}>
              {config?.isSmtpConfigured ? '🟢 SMTP Active' : '🟡 Console Fallback'}
            </span>
          </div>

          <form onSubmit={handleSaveConfig} className="mt-16">
            <div className="form-group mb-14">
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px', color: '#f8fafc' }}>
                Admin Recipient Email (Where alerts are sent):
              </label>
              <input
                type="email"
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
                placeholder="admin@fund.internal"
                required
                style={{ width: '100%', padding: '8px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '14px' }}
              />
              <span className="text-muted small" style={{ display: 'block', marginTop: '4px' }}>
                Receives pending member sign-ups, circuit breaker trips, and investor bookings.
              </span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '10px' }} className="mb-14">
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>
                  SMTP Host:
                </label>
                <input
                  type="text"
                  value={smtpHost}
                  onChange={(e) => setSmtpHost(e.target.value)}
                  placeholder="smtp.gmail.com"
                  style={{ width: '100%', padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>
                  Port:
                </label>
                <input
                  type="number"
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(Number(e.target.value))}
                  placeholder="465"
                  style={{ width: '100%', padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '13px' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="mb-14">
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>
                  SMTP Username:
                </label>
                <input
                  type="text"
                  value={smtpUser}
                  onChange={(e) => setSmtpUser(e.target.value)}
                  placeholder="notifications@domain.com"
                  style={{ width: '100%', padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>
                  SMTP Password / App Key:
                </label>
                <input
                  type="password"
                  value={smtpPass}
                  onChange={(e) => setSmtpPass(e.target.value)}
                  placeholder={config?.hasSmtpPass ? '•••••••• (Saved)' : 'Enter SMTP Password'}
                  style={{ width: '100%', padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '13px' }}
                />
              </div>
            </div>

            <div className="form-group mb-14">
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#94a3b8', marginBottom: '4px' }}>
                Phone Push Topic (ntfy.sh - optional):
              </label>
              <input
                type="text"
                value={ntfyTopic}
                onChange={(e) => setNtfyTopic(e.target.value)}
                placeholder="my-secret-fund-alerts"
                style={{ width: '100%', padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '13px' }}
              />
            </div>

            {/* Alert Categories Checkboxes */}
            <div style={{ background: '#0b0f19', padding: '12px', borderRadius: '6px', border: '1px solid #1e293b' }} className="mb-16">
              <span style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#94a3b8', marginBottom: '8px', textTransform: 'uppercase' }}>
                Attention Events Requiring Notification:
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#f8fafc', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alerts.memberSignup}
                    onChange={(e) => setAlerts({ ...alerts, memberSignup: e.target.checked })}
                  />
                  👤 Member Sign-ups
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#f8fafc', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alerts.riskGovernor}
                    onChange={(e) => setAlerts({ ...alerts, riskGovernor: e.target.checked })}
                  />
                  🚨 Risk Governor Trips
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#f8fafc', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alerts.examPassed}
                    onChange={(e) => setAlerts({ ...alerts, examPassed: e.target.checked })}
                  />
                  🎓 Exam Qualifications
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#f8fafc', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={alerts.investorBooking}
                    onChange={(e) => setAlerts({ ...alerts, investorBooking: e.target.checked })}
                  />
                  📅 Investor Bookings
                </label>
              </div>
            </div>

            {configSuccess && <div className="text-green mb-10 small">{configSuccess}</div>}
            {configError && <div className="text-red mb-10 small">{configError}</div>}

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn btn-primary" disabled={savingConfig}>
                {savingConfig ? 'Saving Settings…' : '💾 Save Notification Settings'}
              </button>
            </div>
          </form>

          {/* Test Dispatch Subcard */}
          <div style={{ borderTop: '1px solid #1e293b', marginTop: '20px', paddingTop: '16px' }}>
            <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#38bdf8' }}>🚀 Test Notification Delivery</h4>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder={adminEmail || 'admin@fund.internal'}
                style={{ flex: 1, padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px', color: '#ffffff', fontSize: '13px' }}
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={testing}
                onClick={handleSendTest}
              >
                {testing ? 'Testing…' : 'Send Test Alert'}
              </button>
            </div>
            {testResult && (
              <div style={{ marginTop: '8px', fontSize: '12.5px', color: testResult.success ? '#10b981' : '#f87171' }}>
                {testResult.success ? '✅' : '❌'} {testResult.message}
              </div>
            )}
          </div>
        </div>

        {/* Card 2: Approvals Summary & Metric Counters */}
        <div className="card">
          <div className="card-head">
            <div>
              <h3 className="card-title">👥 Member Intake Status</h3>
              <p className="card-subtitle text-muted" style={{ margin: '4px 0 0 0' }}>
                Review, approve, and provision platform access for investors & members.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setShowManualIntake(!showManualIntake)}
            >
              {showManualIntake ? '✕ Close Form' : '＋ Add Applicant'}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', margin: '16px 0' }}>
            <div style={{ background: '#0f172a', padding: '14px', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
              <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#fbbf24' }}>{appCounts.pending}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Pending Approval</div>
            </div>
            <div style={{ background: '#0f172a', padding: '14px', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
              <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#10b981' }}>{appCounts.approved}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Approved Members</div>
            </div>
            <div style={{ background: '#0f172a', padding: '14px', borderRadius: '8px', border: '1px solid #1e293b', textAlign: 'center' }}>
              <div style={{ fontSize: '26px', fontWeight: 'bold', color: '#94a3b8' }}>{appCounts.total}</div>
              <div style={{ fontSize: '12px', color: '#94a3b8', textTransform: 'uppercase', marginTop: '2px' }}>Total Applications</div>
            </div>
          </div>

          {/* Quick Manual Intake Form */}
          {showManualIntake && (
            <form onSubmit={handleManualIntake} style={{ background: '#0b0f19', padding: '14px', borderRadius: '8px', border: '1px solid #334155', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#38bdf8' }}>Register Prospective Member</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="mb-10">
                <input
                  type="text"
                  placeholder="Applicant Name"
                  value={intakeName}
                  onChange={(e) => setIntakeName(e.target.value)}
                  required
                  style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: '#fff', fontSize: '13px' }}
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  value={intakeEmail}
                  onChange={(e) => setIntakeEmail(e.target.value)}
                  required
                  style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: '#fff', fontSize: '13px' }}
                />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }} className="mb-10">
                <select
                  value={intakeTier}
                  onChange={(e) => setIntakeTier(e.target.value)}
                  style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: '#fff', fontSize: '13px' }}
                >
                  <option value="Tier 1: Low Risk (Capital Preservation)">Tier 1: Low Risk</option>
                  <option value="Tier 2: Balanced Momentum">Tier 2: Balanced Momentum</option>
                  <option value="Tier 3: Aggressive Multi-Asset Alpha">Tier 3: Aggressive Alpha</option>
                  <option value="Tier 5: Sovereign Syndicate CIO">Tier 5: Sovereign Syndicate</option>
                </select>
                <input
                  type="number"
                  placeholder="Target Capital ($)"
                  value={intakeCapital}
                  onChange={(e) => setIntakeCapital(e.target.value)}
                  style={{ padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: '#fff', fontSize: '13px' }}
                />
              </div>
              <textarea
                placeholder="Investor notes / strategy objective..."
                value={intakeNotes}
                onChange={(e) => setIntakeNotes(e.target.value)}
                rows={2}
                style={{ width: '100%', padding: '6px 10px', background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', color: '#fff', fontSize: '13px', marginBottom: '10px' }}
              />
              <button type="submit" className="btn btn-sm btn-primary" disabled={submittingIntake}>
                {submittingIntake ? 'Registering…' : 'Submit & Notify Admin'}
              </button>
            </form>
          )}

          <div style={{ background: '#0b0f19', padding: '14px', borderRadius: '8px', border: '1px solid #1e293b' }}>
            <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#94a3b8' }}>💡 Member Sign-up Workflow:</h4>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#cbd5e1', lineHeight: '1.6' }}>
              <li>Prospective investors submit application via the client portal or Cal.com webhook.</li>
              <li>Admin instantly receives high-priority email notification with applicant profile.</li>
              <li>Clicking <strong>Approve & Issue Invite</strong> provisions a secure 12-digit code and automatically emails the member their access key.</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Main Section: Member Applications Table */}
      <div className="card mb-20">
        <div className="card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h3 className="card-title">👤 Member Applications & Approval Queue</h3>
            <p className="card-subtitle text-muted" style={{ margin: '4px 0 0 0' }}>
              Manage membership access requests, review risk mandates, and issue authorized invite codes.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className={`btn btn-sm ${filterStatus === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setFilterStatus('all'); loadApplications('all'); }}
            >
              All ({appCounts.total})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filterStatus === 'pending_approval' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setFilterStatus('pending_approval'); loadApplications('pending_approval'); }}
            >
              Pending ({appCounts.pending})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filterStatus === 'approved' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setFilterStatus('approved'); loadApplications('approved'); }}
            >
              Approved ({appCounts.approved})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${filterStatus === 'rejected' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => { setFilterStatus('rejected'); loadApplications('rejected'); }}
            >
              Rejected ({appCounts.rejected})
            </button>
          </div>
        </div>

        {loadingApps ? (
          <div className="loading-state">Loading applications…</div>
        ) : filteredApps.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '36px 20px', color: '#94a3b8' }}>
            <div style={{ fontSize: '32px', marginBottom: '8px' }}>📭</div>
            <strong>No applications in this category.</strong>
            <p className="small" style={{ margin: '4px 0 0 0' }}>
              When prospective members apply for membership or book a consultation, they will appear here awaiting approval.
            </p>
          </div>
        ) : (
          <div className="table-responsive mt-16">
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>Mandate Tier</th>
                  <th>Target Capital</th>
                  <th>Applied On</th>
                  <th>Status</th>
                  <th>Invite Code</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApps.map((app) => (
                  <tr key={app.id}>
                    <td>
                      <div style={{ fontWeight: 600, color: '#f8fafc' }}>{app.name}</div>
                      <div className="small text-muted">{app.email}</div>
                      {app.notes && (
                        <div className="small" style={{ color: '#94a3b8', fontStyle: 'italic', marginTop: '2px' }}>
                          "{app.notes}"
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge badge--blue" style={{ fontSize: '11px' }}>
                        {app.riskTier}
                      </span>
                    </td>
                    <td>
                      <strong style={{ color: '#10b981' }}>
                        ${app.targetInvestment.toLocaleString()}
                      </strong>
                    </td>
                    <td>
                      <span className="small text-muted">
                        {new Date(app.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`badge ${
                          app.status === 'approved'
                            ? 'badge--green'
                            : app.status === 'pending_approval'
                            ? 'badge--yellow'
                            : 'badge--red'
                        }`}
                      >
                        {app.status === 'pending_approval'
                          ? '⏳ Pending Review'
                          : app.status === 'approved'
                          ? '✅ Approved'
                          : '❌ Rejected'}
                      </span>
                    </td>
                    <td>
                      {app.inviteCode ? (
                        <span style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#38bdf8' }}>
                          {app.inviteCode}
                        </span>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {app.status === 'pending_approval' ? (
                        <div style={{ display: 'inline-flex', gap: '6px' }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleApprove(app)}
                            title="Approve and issue invite code"
                          >
                            ✅ Approve
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-secondary"
                            onClick={() => handleReject(app)}
                            title="Reject application"
                          >
                            Reject
                          </button>
                        </div>
                      ) : app.status === 'approved' ? (
                        <span className="small text-green">Invite Active</span>
                      ) : (
                        <span className="small text-muted">{app.rejectionReason || 'Rejected'}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Dispatched Notification History Log */}
      <div className="card">
        <div className="card-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 className="card-title">📋 Admin Attention & Notification Activity Log</h3>
            <p className="card-subtitle text-muted" style={{ margin: '4px 0 0 0' }}>
              Real-time audit trail of all alerts dispatched to administrators.
            </p>
          </div>
          <button type="button" className="btn btn-sm btn-secondary" onClick={loadLogs}>
            🔄 Refresh Log
          </button>
        </div>

        {loadingLogs ? (
          <div className="loading-state">Loading notification log…</div>
        ) : logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8' }}>
            No notification activity recorded yet.
          </div>
        ) : (
          <div className="table-responsive mt-16">
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Alert Title</th>
                  <th>Category</th>
                  <th>Recipient</th>
                  <th>Channel</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <span
                        className={`badge ${
                          log.status === 'sent'
                            ? 'badge--green'
                            : log.status === 'simulated'
                            ? 'badge--blue'
                            : 'badge--red'
                        }`}
                        style={{ fontSize: '11px' }}
                      >
                        {log.status === 'sent' ? '🟢 Sent' : log.status === 'simulated' ? '🟡 Simulated' : '🔴 Failed'}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500, color: '#f8fafc' }}>
                      {log.title}
                      {log.error && <div className="small text-red">{log.error}</div>}
                    </td>
                    <td>
                      <span className="badge badge--yellow" style={{ fontSize: '11px' }}>
                        {log.kind}
                      </span>
                    </td>
                    <td className="small text-muted">{log.recipient}</td>
                    <td>
                      <span style={{ fontSize: '12px', color: '#38bdf8', textTransform: 'uppercase' }}>
                        {log.channel}
                      </span>
                    </td>
                    <td className="small text-muted">
                      {new Date(log.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
